const ALPHANUMERIC_REGEX = new RegExp('^[a-zA-Z0-9]*$', 'i');
const ALPHANUMERIC_ACCENT_REGEX = new RegExp(
    "^[\\p{L}\\p{M}\\p{N}\\-\\s'.]+$",
    'ui',
);

const ALPHA_ONLY_REGEX = new RegExp("^[\\p{L}\\p{M}\\-\\s'.]+$", 'ui');
const ALPHA_SPACE_HYPHEN_REGEX = new RegExp("^[\\p{L}\\p{M}\\-\\s'.]+$", 'ui');
const NUMERIC_REGEX = new RegExp('^[0-9]*$');
const FREQUENT_FLYER_ALPHA_DIGIT_REGEX = /^[A-Za-z]{2}\d{9,}$/;
const ADDRESS_REGEX = new RegExp(
    "^[\\p{L}\\p{M}\\p{N}\\-\\s'.,#()/@&:]+$",
    'ui',
);
const EMAIL_REGEX =
    // eslint-disable-next-line no-control-regex
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i;
const ALPHABET_REGEX = /.*[a-zA-Z].*/;
const VALID_PHONENUMBER_REGEX = /^[0-9]{10,}$/;
const INVALID_CHARACTERS_REGEX = /[\\<>]|src/i;
const AIRTAG_REGEX_PROD = /^https:\/\/find\.apple\.com\/item\?i=/;
const AIRTAG_REGEX_CHINA_PROD = /^https:\/\/find\.apple\.com\.cn\/item\?i=/;

export const PHONE_MIN_LENGTH = 8;
export const EMAIL_MIN_LENGTH = 6;
export const STATE_MIN_LENGTH = 2;
export const MIN_ZIP_CODE_LENGTH = 5;
export const PNR_MIN_LENGTH = 6;
export const RESERVATION_NUMBER_MIN_LENGTH = 6;
export const FLIGHT_NUMBER_MAX_LENGTH = 4;
export const FLIGHT_NUMBER_MIN_LENGTH = 1;
export const MAX_TAGS_PER_PASSENGER = 4;
export const MIN_PASSIVE_DAYS = 28;
export const MAX_PASSIVE_DAYS = 30;
export const MIN_DAYS_BEFORE_ARRIVAL = 1;
export const MAX_DAYS_BEFORE_ARRIVAL = 30;

export function isPhoneLongEnough(value) {
    if (!value) {
        return false;
    }

    return value.length >= PHONE_MIN_LENGTH;
}

function isRepeating(value) {
    if (!value) {
        return false;
    }

    return new Set(value).size === 1;
}

export function isAddressValid(value) {
    if (!value) {
        return false;
    }

    return ADDRESS_REGEX.test(value);
}

export function isPhoneValid(value) {
    if (!value) {
        return false;
    }

    return VALID_PHONENUMBER_REGEX.test(value) && !isRepeating(value);
}

export function isZipCodeValid(value) {
    if (!value) {
        return false;
    }

    return isNumeric(value) && value.length === MIN_ZIP_CODE_LENGTH;
}

export function hasLetters(value) {
    if (!value) {
        return false;
    }

    return ALPHABET_REGEX.test(value);
}

export function isEmailLongEnough(value) {
    if (!value) {
        return false;
    }

    return value.length >= EMAIL_MIN_LENGTH;
}

export function isStateLongEnough(value) {
    if (!value) {
        return false;
    }

    return value.length >= STATE_MIN_LENGTH;
}

export function isReservationNumberLongEnough(value) {
    if (!value) {
        return false;
    }

    return value.length >= RESERVATION_NUMBER_MIN_LENGTH;
}

export function isAlphanumeric(value) {
    if (!value) {
        return true;
    }

    return ALPHANUMERIC_REGEX.test(value);
}

export function isAlphanumericAccent(value) {
    if (!value) {
        return true;
    }

    return ALPHANUMERIC_ACCENT_REGEX.test(value);
}

export function isAlpha(value) {
    if (!value) {
        return true;
    }

    return ALPHA_ONLY_REGEX.test(value);
}

export function isAlphaSpaceHyphen(value) {
    if (!value) {
        return true;
    }
    return ALPHA_SPACE_HYPHEN_REGEX.test(value);
}

export function isNumeric(value) {
    if (!value) {
        return false;
    }

    return NUMERIC_REGEX.test(value);
}

export function isEmailValidFormat(value) {
    if (!value) {
        return false;
    }

    return EMAIL_REGEX.test(value);
}

export function isAirTagUrlValidFormat(value, tracerBags, bagTag) {
    if (!value) {
        return true;
    }

    return (
        (AIRTAG_REGEX_PROD.test(value) ||
            AIRTAG_REGEX_CHINA_PROD.test(value)) &&
        tracerBags?.find(
            (bag) => bag.appleAirTagUrl === value && bag.tagNumber !== bagTag,
        ) === undefined
    );
}

export function isPnrValid(value) {
    if (!value) {
        return false;
    }
    return isAlphanumeric(value) && isPnrLongEnough(value);
}

export function isPnrLongEnough(value) {
    if (!value) {
        return false;
    }

    return value.length === PNR_MIN_LENGTH;
}

export function isInputContainingInvalidCharacters(value) {
    return INVALID_CHARACTERS_REGEX.test(value);
}

export function getPhoneErrorMessage(t, field, value) {
    if (!value) {
        return '';
    }

    if (!isPhoneLongEnough(value)) {
        return `${t('inputTooShort', { length: PHONE_MIN_LENGTH })}`;
    } else if (!isPhoneValid(value)) {
        return `${t(field)} ${t('inputInvalid')}`;
    }
}

export function isFrequentFlyerFormatValid(value) {
    if (!value) {
        return true;
    }

    return FREQUENT_FLYER_ALPHA_DIGIT_REGEX.test(value);
}

export function getFrequentFlyerErrorMessage(t, value) {
    if (!value) {
        return '';
    }

    if (!isAlphanumeric(value)) {
        return `${t('frequentFlyerNotAlphaNumeric')} `;
    } else if (!isFrequentFlyerFormatValid(value)) {
        return `${t('frequentFlyerMissingStartingLetters')} ${t(
            'frequentFlyerNotLongEnough',
        )}`;
    }
}

export function getEmailErrorMessage(t, value) {
    if (!value) {
        return '';
    }

    if (!isEmailValidFormat(value)) {
        return `${t('emailAddress')} ${t('inputInvalid')}`;
    } else if (!isEmailLongEnough(value)) {
        return `${t('inputTooShort', { length: EMAIL_MIN_LENGTH })}`;
    }
}

export function getStateErrorMessage(t, value) {
    if (!value) {
        return '';
    }

    if (!isAlpha(value)) {
        return `${t('state')} ${t('inputInvalid')}`;
    } else if (!isStateLongEnough(value)) {
        return `${t('inputTooShort', { length: STATE_MIN_LENGTH })}`;
    }
}

export function getPnrErrorMessage(t, value) {
    if (!value) {
        return '';
    }

    if (!isAlphanumeric(value)) {
        return `${t('bookingReference')} ${t('inputInvalid')}`;
    } else if (!isPnrLongEnough(value)) {
        return `${t('inputTooShort', { length: PNR_MIN_LENGTH })}`;
    }
}

export function isLastNameValid(lastName) {
    return lastName === '' || lastName.trim() === '' || isAlpha(lastName);
}

export function isClaimNumberAndLastNamePopulated(claimNumber, lastName) {
    return (
        claimNumber !== undefined &&
        claimNumber !== '' &&
        lastName !== undefined &&
        lastName !== ''
    );
}

export function isValidityDateValid(validityDate) {
    const currentDate = new Date();
    return (
        getUtcDate(new Date(validityDate)).setHours(0, 0, 0, 0) >=
        getUtcDate(currentDate).setHours(0, 0, 0, 0)
    );
}

export function isFlightNumberValid(flightNumber) {
    if (!flightNumber) {
        return false;
    }

    return (
        flightNumber.length <= FLIGHT_NUMBER_MAX_LENGTH &&
        flightNumber.length >= FLIGHT_NUMBER_MIN_LENGTH &&
        isNumeric(flightNumber)
    );
}

export function isArrivalDateValid(arrivalDate) {
    const currentDate = new Date();
    const minDate = new Date(
        new Date().setDate(currentDate.getDate() + MIN_DAYS_BEFORE_ARRIVAL),
    );
    const maxDate = new Date(
        new Date().setDate(currentDate.getDate() + MAX_DAYS_BEFORE_ARRIVAL),
    );

    return (
        getUtcDate(minDate).setHours(0, 0, 0, 0) <=
            getUtcDate(new Date(arrivalDate)).setHours(0, 0, 0, 0) &&
        getUtcDate(new Date(arrivalDate)).setHours(0, 0, 0, 0) <=
            getUtcDate(maxDate).setHours(0, 0, 0, 0)
    );
}

export function isPassiveRange(arrivalDate, startDays, endDays) {
    const currentDate = new Date();
    const minDate = new Date(
        new Date().setDate(currentDate.getDate() + startDays),
    );
    const maxDate = new Date(
        new Date().setDate(currentDate.getDate() + endDays),
    );

    return (
        getUtcDate(minDate).setHours(0, 0, 0, 0) <=
            getUtcDate(new Date(arrivalDate)).setHours(0, 0, 0, 0) &&
        getUtcDate(new Date(arrivalDate)).setHours(0, 0, 0, 0) <=
            getUtcDate(maxDate).setHours(0, 0, 0, 0)
    );
}

export function flightArrivalDateMatchesResortArrivalDate(
    flightDate,
    arrivalDate,
) {
    if (!arrivalDate || !flightDate) return false;
    const resortArrival = getUtcDate(new Date(arrivalDate)).setHours(
        0,
        0,
        0,
        0,
    );
    const flightArrival = getUtcDate(new Date(flightDate)).setHours(0, 0, 0, 0);
    return flightArrival === resortArrival;
}

export function getUtcDate(localDate) {
    return new Date(
        localDate.getTime() + localDate.getTimezoneOffset() * 60000,
    );
}
